import { useState, useRef } from "react";
import { account } from "@/lib/appwrite";
import { SUBJECTS_DATA } from "@/data/subjects";
import {
  Sparkles, ChevronDown, Loader2, CheckCircle2, AlertCircle,
  Square, ArrowLeft, RotateCcw,
} from "lucide-react";

interface Progress {
  processed: number;
  updated: number;
  errors: number;
  total: number;
}

export function AdminRefinePage() {
  const [subjectId, setSubjectId] = useState("");
  const [moduleId, setModuleId]   = useState("");
  const [batchSize, setBatchSize] = useState(50);

  const [running, setRunning]     = useState(false);
  const [done, setDone]           = useState(false);
  const [progress, setProgress]   = useState<Progress | null>(null);
  const [log, setLog]             = useState<string[]>([]);
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null);
  const logRef    = useRef<HTMLDivElement>(null);

  const selectedSubject = SUBJECTS_DATA.find(s => s.id === subjectId);

  function appendLog(line: string) {
    setLog(prev => {
      const next = [...prev, line];
      return next.slice(-200); // keep last 200 lines
    });
    setTimeout(() => {
      if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    }, 0);
  }

  async function start() {
    setRunning(true);
    setDone(false);
    setProgress(null);
    setLog([]);

    try {
      const { jwt } = await account.createJWT();
      const res = await fetch("/api/admin/refine-explanations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ subjectId, moduleId, batchSize }),
      });

      if (!res.ok || !res.body) {
        appendLog(`Error: ${res.status} ${res.statusText}`);
        setRunning(false);
        return;
      }

      const reader = res.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const msg = JSON.parse(line.slice(6));

            if (msg.type === "progress") {
              setProgress(msg);
              appendLog(`[${msg.processed}/${msg.total}] refined: ${msg.updated}  errors: ${msg.errors}`);
            } else if (msg.type === "error") {
              appendLog(`  ✗ error on ${msg.id}: ${msg.msg}`);
            } else if (msg.type === "fatal") {
              appendLog(`Fatal: ${msg.msg}`);
            } else if (msg.type === "done") {
              setProgress(msg);
              appendLog(`\nDone — ${msg.updated} explanations replaced, ${msg.errors} errors.`);
              setDone(true);
            }
          } catch {}
        }
      }
    } catch (e: any) {
      appendLog(`Connection error: ${e.message}`);
    } finally {
      setRunning(false);
      readerRef.current = null;
    }
  }

  function stop() {
    readerRef.current?.cancel();
    readerRef.current = null;
    setRunning(false);
    appendLog("— stopped by user —");
  }

  const pct = progress && progress.total > 0
    ? Math.round((progress.processed / progress.total) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-[#f8f9fc]">

      {/* Header */}
      <div className="bg-white border-b border-[#dde3ed] px-6 py-4 flex items-center gap-3 sticky top-0 z-10 shadow-sm">
        <button
          onClick={() => window.location.href = "/admin"}
          className="text-[#5a6a7e] hover:text-[#1a2435] text-sm font-medium transition-colors"
        >
          ← Admin
        </button>
        <div className="h-4 w-px bg-[#dde3ed]" />
        <Sparkles className="w-4 h-4 text-[#f5a623]" />
        <h1 className="text-base font-semibold text-[#1a2435]">Refine Explanations</h1>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">

        {/* Config */}
        <div className="bg-white rounded-2xl border border-[#dde3ed] p-6 shadow-sm space-y-4">
          <h2 className="text-sm font-semibold text-[#1a2435]">Configuration</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#5a6a7e] mb-1 block">Subject</label>
              <div className="relative">
                <select
                  value={subjectId}
                  onChange={e => { setSubjectId(e.target.value); setModuleId(""); }}
                  disabled={running}
                  className="w-full appearance-none border border-[#dde3ed] rounded-xl px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 disabled:opacity-50"
                >
                  {SUBJECTS_DATA.map(s => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-[#5a6a7e] pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[#5a6a7e] mb-1 block">Module</label>
              <div className="relative">
                <select
                  value={moduleId}
                  onChange={e => setModuleId(e.target.value)}
                  disabled={running}
                  className="w-full appearance-none border border-[#dde3ed] rounded-xl px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 disabled:opacity-50"
                >
                  <option value="">All modules</option>
                  {(selectedSubject?.modules ?? []).map(m => (
                    <option key={m.id} value={m.id}>{m.title}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-[#5a6a7e] pointer-events-none" />
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[#5a6a7e] mb-1 block">
              Batch size — max questions to process this run
            </label>
            <input
              type="number"
              min={1}
              max={500}
              value={batchSize}
              onChange={e => setBatchSize(Math.max(1, Math.min(500, parseInt(e.target.value) || 50)))}
              disabled={running}
              className="w-32 border border-[#dde3ed] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 disabled:opacity-50"
            />
            <p className="text-xs text-[#5a6a7e] mt-1">
              Every question in the selected scope gets a fresh explanation from Claude. Run multiple times to cover all questions.
            </p>
          </div>

          <div className="flex gap-3 pt-1">
            {!running ? (
              <button
                onClick={start}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#1a3a6b] text-white text-sm font-semibold rounded-xl hover:bg-[#0f2548] transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                {done ? "Run Again" : "Start Refining"}
              </button>
            ) : (
              <button
                onClick={stop}
                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors"
              >
                <Square className="w-4 h-4 fill-white" />
                Stop
              </button>
            )}
          </div>
        </div>

        {/* Progress */}
        {progress && (
          <div className="bg-white rounded-2xl border border-[#dde3ed] p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-[#1a2435]">
                {done ? "Complete" : "Running…"}
              </span>
              <span className="text-[#5a6a7e]">{progress.processed} / {progress.total}</span>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-[#f0f2f5] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#1a3a6b] rounded-full transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Replaced", value: progress.updated,                 color: "text-green-600", bg: "bg-green-50" },
                { label: "Remaining",value: progress.total - progress.processed, color: "text-[#5a6a7e]", bg: "bg-[#f8f9fc]" },
                { label: "Errors",   value: progress.errors,                  color: "text-red-600",   bg: "bg-red-50" },
              ].map(s => (
                <div key={s.label} className={`${s.bg} rounded-xl px-4 py-3 text-center`}>
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-[#5a6a7e] mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {done && (
              <div className="flex items-center gap-2 text-green-700 text-sm bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                Finished — {progress.updated} explanations replaced.
              </div>
            )}
          </div>
        )}

        {/* Log */}
        {log.length > 0 && (
          <div className="bg-[#0d1117] rounded-2xl border border-[#30363d] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#30363d]">
              <span className="text-xs font-medium text-[#8b949e]">Log</span>
              <button
                onClick={() => setLog([])}
                className="text-xs text-[#8b949e] hover:text-white transition-colors"
              >
                Clear
              </button>
            </div>
            <div
              ref={logRef}
              className="px-4 py-3 h-56 overflow-y-auto font-mono text-xs text-[#e6edf3] space-y-0.5"
            >
              {log.map((line, i) => (
                <div key={i} className={
                  line.startsWith("Done") || line.startsWith("Finished") ? "text-green-400" :
                  line.startsWith("Fatal") || line.includes("✗") ? "text-red-400" :
                  line.startsWith("—") ? "text-[#8b949e]" : ""
                }>
                  {line}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
